import { FilterCondition, PagedResultDTOOfParkingSubscriptionDTO, ParkingSubscriptionType } from './../../../base/models/models.swagger.generated';
import { ErrorType } from '../../../core/routing/error-types';
import { catchError, tap } from 'rxjs/operators';
import { ToastService } from 'src/app/base/services/toast.service';
import { ClientService } from '../../../base/services/client/client.service';
import {
  FilterDTO,
  FilterOperator,
  PagedResultDTOOfParkerDTO,
  ParkerClient,
  ParkerDTO,
  ParkingSubscriptionClient,
  ParkingSubscriptionDTO,
  SortDirection,
} from '../../../base/models/models.swagger.generated';
import { IAdaptorService } from '../../../base/shared/syncfusion/adaptor-service.interface';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { FilterUtil } from 'src/app/base/shared/filter-util';

export const zipField = 'zip';

@Injectable()
export class ParkerAdministrationService extends ShowInactiveService implements IAdaptorService {

  constructor(private parkerClient: ParkerClient,
    private parkingSubscriptionClient: ParkingSubscriptionClient,
    private clientService: ClientService,
    private toastService: ToastService,
    private spinnerService: SpinnerService) {
    super();
  }
  public subscriptionAssignmentFieldName = 'parkerSubscriptionAssignments';

  public filters: FilterDTO[] = null;
  public parkerIdToFilter: string = null;
  public numTotalRecordsFound = 0;
  public parkingSubscriptions: ParkingSubscriptionDTO[] = null;
  private _lastFiltersFromGrid: FilterDTO[] = null;

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<PagedResultDTOOfParkerDTO> {
    // Adjust zip filter name to match backend
    FilterUtil.FindAllFiltersWithSpecificField(zipField, filters).forEach(f => {
      f.field = 'ZIP';
    });

    this._lastFiltersFromGrid = filters ? [...filters] : [];
    if (!this.clientService.selectedClient) {
      return of(null);
    }
    if (!sortBy) {
      sortBy = 'Id';
    }
    if (!filters) {
      filters = this.filters ?? [];
    }
    var existingFilterIndex = filters.findIndex(f => f.field === 'Active');
    if (!this.showInactive) {
      if (existingFilterIndex == -1) {
        filters.push(new FilterDTO({ isComplex: false, field: 'Active', operator: FilterOperator.Equal, value: 'true' }));
      }
    }
    else {
      if (existingFilterIndex > -1) {
        filters.splice(existingFilterIndex, 1);
      }
    }
    const fieldName = this.subscriptionAssignmentFieldName.toLocaleLowerCase();
    filters.forEach(f => {
      this.replaceSubscriptionFieldInFilter(f, this.subscriptionAssignmentFieldName, 'ParkerSubscriptionAssignments.Subscription.Id');
    });
    var subscriptionAssignmentFilters = filters.filter(f => !f.isComplex && f.field?.toLocaleLowerCase() === fieldName);
    subscriptionAssignmentFilters.forEach(f => {
      f.field = 'ParkerSubscriptionAssignments.Subscription.Id';
    });

    if (this.parkerIdToFilter) {
      filters.push(new FilterDTO({ isComplex: false, field: 'Id', operator: FilterOperator.Equal, value: this.parkerIdToFilter.toString() }));
    }

    return this.parkerClient.searchParkers(this.clientService.selectedClient.id, filters, skip, take, sortBy, sortDirection, true, false).pipe(
      tap((result) => {
        if (result.items) {
          // avoid null pointer exceptions in filters
          result.items.forEach((item) => {
            if (!item.costCenter) {
              item.costCenter = '';
            }
            if (!item.title) {
              item.title = '';
            }
            if (!item.company) {
              item.company = { name: '' } as any;
            }
          });
        }
        this.numTotalRecordsFound = result.totalNumberOfItems;
      })
    );
  }

  public addRecord(data: any): Observable<ParkerDTO> {
    data.clientId = this.clientService.selectedClient?.id;
    return this.parkerClient.createParker(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.parker-administration.messages.add-title',
          'i18n.domain.parker-administration.messages.add-success-message'
        );
      }),
      catchError((error) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showApiExeption(error, 'i18n.errors.server-error.title', 'i18n.errors.server-error.message');
        }
        return throwError(() => error);
      }));
  }

  public updateRecord(data: any): Observable<ParkerDTO> {
    return this.parkerClient.updateParker(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.parker-administration.messages.update-title',
          'i18n.domain.parker-administration.messages.update-success-message'
        );
      }),
      catchError(error => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showApiExeption(error, 'i18n.errors.server-error.title', 'i18n.errors.server-error.message');
        }
        return throwError(() => error);
      }));
  }

  public updateParkers(data: any[]):Observable<ParkerDTO[]> {
   return forkJoin([...data.map(updateParker => this.parkerClient.updateParker(updateParker))]);
  }

  public deleteRecord(id: number): Observable<void> {
    return this.parkerClient.deleteParker(id).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.parker-administration.messages.delete-title',
          'i18n.domain.parker-administration.messages.delete-success-message'
        );
      }),
      catchError(error => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public initializeParkerToUserLink(parkerId: number): Observable<void> {
    this.spinnerService.showSpinner = true;
    return this.parkerClient.initializeParkerToUserLink(parkerId).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.parker-administration.messages.initialize-link-title',
          'i18n.domain.parker-administration.messages.initialize-link-success-message'
        );
      }),
      catchError(error => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public unlinkUserFromParker(parkerId: number): Observable<void> {
    this.spinnerService.showSpinner = true;
    return this.parkerClient.unlinkParkerFromUser(parkerId).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.parker-administration.messages.unlink-title',
          'i18n.domain.parker-administration.messages.unlink-success-message'
        );
      }),
      catchError(error => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public loadSubscriptions(): Observable<PagedResultDTOOfParkingSubscriptionDTO> {
    const clientId = this.clientService.selectedClient?.id;
    if (!clientId) {
      return of(null);
    }
    // only standard subscriptions are assignable, so only look for those
    const filters = [
      new FilterDTO({
        isComplex: false,
        field: 'Assignable',
        operator: FilterOperator.Equal,
        value: 'true',
      }),
      new FilterDTO({
        isComplex: false,
        field: 'Type',
        operator: FilterOperator.NotEqual,
        value: ParkingSubscriptionType.ForeignParkerSubscription.toString()
      })
    ];
    return this.parkingSubscriptionClient.searchParkingSubscriptions(clientId, filters, 0, 1000, 'Name', SortDirection.Descending).pipe(tap(result => {
      result.items.forEach(subscription => {
        let cnt = 1;
        result.items.filter(s => s.id !== subscription.id && s.name === subscription.name).forEach(s => {
          s.name = subscription.name + ' (' + cnt + ')';
          cnt++;
        });
      });
      this.parkingSubscriptions = result.items;
    }));
  }

  public loadPagedParkersForFunctions(skip: number, take: number): Observable<PagedResultDTOOfParkerDTO> {
    return this.getData(skip, take, this._lastFiltersFromGrid, null, SortDirection.Ascending);
  }

  private replaceSubscriptionFieldInFilter(filter: FilterDTO, oldFieldName: string, newFieldName: string): void {
    const oldFieldNameLower = oldFieldName.toLocaleLowerCase();
    if (filter.field?.toLocaleLowerCase() === oldFieldNameLower) {
      filter.field = newFieldName;
      const subscription = this.parkingSubscriptions.find(s => s.name === filter.value);
      if (subscription) {
        filter.value = subscription.id.toString();
      }
    }
    if (filter.predicates) {
      filter.predicates.forEach(f => this.replaceSubscriptionFieldInFilter(f, oldFieldName, newFieldName));
    }
  }
}
